import { FilterOperator } from '@helo/cms'
import { FilterModel, SortModelItem } from 'ag-grid-enterprise'

import { getAgGridSortModelFromColumnState } from '../../components/Table/agGridSortPriority.utils'
import { FilterCriteria } from '../../services/queryService'
import { AgGridFilterCriteria, AgGridFilterModel } from './table.types'

export { getAgGridSortModelFromColumnState }

export enum DateFilterOptions {
  equals = 'equals',
  greaterthan = 'greaterthan',
  inrange = 'inrange',
  lessthan = 'lessthan',
  notequal = 'notequal',
}

/**
 * Remove object keys not matching filter function.
 *
 * Defaults to removing undefined values.
 */
export const objectFilter = <T extends Record<string, any>>(
  obj: T,
  compare: (value: any, key: string) => boolean = value => value !== undefined,
) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => compare(value, key)),
  )

const agGridFilterToToHeloFilter = (filterCriteria: AgGridFilterCriteria) => {
  const {
    type: condition,
    filterType,
    dateFrom,
    dateTo,
    filter,
    filterTo,
  } = filterCriteria

  const isDateType = filterType?.toLowerCase() === 'date'
  // Note: not sure what the purpose of this is.
  const isConditionInDateFilterOptions =
    !!DateFilterOptions[(condition?.toLowerCase() || '') as DateFilterOptions]

  if (isDateType && isConditionInDateFilterOptions) {
    return { filterValue: dateFrom, filterToValue: dateTo }
  }

  return { filterValue: filter, filterToValue: filterTo }
}

/**
 * needs better name. Reverses back into ag grid filter schema.
 */
const heloFilterToAgGridFilterTo = (
  filterCriteria: Omit<FilterCriteria, 'columnId'>,
) => {
  const { filterValue, filterToValue, filterValueType } = filterCriteria

  const isDateType = filterValueType?.toLowerCase() === 'date'
  if (isDateType) {
    return {
      dateFrom: filterValue,
      dateTo: filterToValue,
    }
  }
  // defensively code against bad data sent fro backend.
  if (filterValueType?.toLocaleLowerCase() === 'number') {
    return {
      filter: filterValue && Number(filterValue),
      filterTo: filterToValue && Number(filterToValue),
    }
  }

  return {
    filter: filterValue,
    filterTo: filterToValue,
  }
}

/**
 * Converts AGGrid filter schema to the API filter schema.
 */
const convertToHeloFilterCriteria = (
  columnId: string,
  filterCriteria: AgGridFilterCriteria,
  isChildCondition = false,
): FilterCriteria | undefined => {
  if (!filterCriteria) return undefined

  const {
    operator,
    type: condition,
    filterType: filterValueType,
    condition1,
    condition2,
  } = filterCriteria

  const filterValuesFromTo = agGridFilterToToHeloFilter(filterCriteria)
  const { filterValue, filterToValue } = filterValuesFromTo

  const filterStructure: Partial<FilterCriteria> = {
    columnId: isChildCondition ? undefined : columnId,
    operator: isChildCondition ? undefined : operator || FilterOperator.AND,
    condition,
    filterValue,
    filterToValue,
    filterValueType,
    operand1: convertToHeloFilterCriteria(columnId, condition1, true),
    operand2: convertToHeloFilterCriteria(columnId, condition2, true),
  }

  return objectFilter(
    filterStructure,
    value => value !== null && value !== undefined,
  ) as FilterCriteria
}

/**
 * covnerts helo filter schema to ag grid filter schema.
 */
const filterCriteriaToFilterModel = (
  filterCriteria: Omit<FilterCriteria, 'columnId'>,
): AgGridFilterCriteria => {
  const { operator, condition, filterValueType, operand1, operand2 } =
    filterCriteria

  const filterModel = {
    // There needs to be subconditions for operator to be applied (AND, OR)
    operator: operand1 ? operator : undefined,
    type: condition,
    filterType: filterValueType,
    ...heloFilterToAgGridFilterTo(filterCriteria),
    condition1: operand1 && filterCriteriaToFilterModel(operand1),
    condition2: operand2 && filterCriteriaToFilterModel(operand2),
  } as any

  return objectFilter(filterModel) as any
}

/**
 * Converts AG Grid sortModel to Electrum orderBy format
 * AG Grid format: [{ colId: 'fieldName', sort: 'asc' | 'desc' }]
 * Electrum format: [{ fieldName: 'ASC' | 'DESC' }]
 *
 * @param sortModel - AG Grid sort model
 */
export const agGridSortModelToOrderBy = (
  sortModel: SortModelItem[],
): Array<Record<string, 'ASC' | 'DESC'>> => {
  if (!sortModel || sortModel.length === 0) {
    return []
  }

  return sortModel
    .filter(
      (item): item is SortModelItem =>
        item !== null && item !== undefined && !!item.colId && !!item.sort,
    )
    .map(item => {
      const direction = item.sort === 'asc' ? 'ASC' : 'DESC'
      return {
        [item.colId]: direction,
      }
    })
}

/**
 * Converts Electrum orderBy format to AG Grid sortModel
 * Electrum format: [{ fieldName: 'ASC' | 'DESC' }]
 * AG Grid format: [{ colId: 'fieldName', sort: 'asc' | 'desc' }]
 *
 * @param orderBy - Electrum orderBy format
 */
export const orderByToAgGridSortModel = (
  orderBy: Array<Record<string, 'ASC' | 'DESC'>> | null | undefined,
): SortModelItem[] => {
  if (!orderBy || orderBy.length === 0) {
    return []
  }

  return orderBy
    .filter(item => item && typeof item === 'object')
    .map((item, index) => {
      const entries = Object.entries(item)
      if (entries.length === 0) {
        return null
      }

      const [fieldName, direction] = entries[0]
      const sort = direction === 'ASC' ? 'asc' : 'desc'

      return {
        colId: fieldName,
        sort,
        sortIndex: index,
      } as SortModelItem
    })
    .filter((item): item is SortModelItem => item !== null)
}

export const heloToAgGridFilter = (
  filterCriteria: FilterCriteria[],
): AgGridFilterModel =>
  filterCriteria.reduce(
    (acc, criteria) => ({
      ...acc,
      [criteria.columnId]: filterCriteriaToFilterModel(criteria),
    }),
    {} as AgGridFilterModel,
  )

export const agGridToHeloApiFilter = (
  filterModel: FilterModel,
): FilterCriteria[] =>
  Object.entries<AgGridFilterCriteria>(filterModel).map(([key, value]) =>
    convertToHeloFilterCriteria(key, value),
  ) as FilterCriteria[]
